import React, { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import Button from "../../../components/Buttons/Button/Button";
import ButtonClose from "../../../components/Buttons/ButtonClose/ButtonClose";
import Combobox from "../../../components/Combobox/Combobox";
import { getCreditPackagesDropdown, createCreditPackagesMultiCheckout } from "../../../../services/subscriptionsService";
import "./CreditPackagesModal.css";

function CreditPackagesModal({ open, t, onClose }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    const loadPackages = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCreditPackagesDropdown();
        if (!isMounted) return;
        setPackages(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) {
          const first = data[0];
          setSelectedPackageId(String(first.id ?? first.credit_package_id ?? ""));
        }
      } catch (e) {
        if (!isMounted) return;
        setError(t("page.creditPackages.errorLoading"));
        setPackages([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPackages();

    return () => {
      isMounted = false;
    };
  }, [open, t]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSubmitting(false);
      setQuantity(1);
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!selectedPackageId || !quantity || Number(quantity) <= 0) {
      setError(t("page.creditPackages.invalidForm"));
      return;
    }

    const normalizedQuantity = Math.max(1, Number(quantity) || 1);

    setSubmitting(true);
    setError(null);
    try {
      const creditPackageId = Number(selectedPackageId);
      const payloadItems = [
        {
          credit_package_id: creditPackageId,
          quantity: normalizedQuantity,
        },
      ];

      const data = await createCreditPackagesMultiCheckout(payloadItems);
      const url = data?.url || data?.checkout_url || data?.checkoutUrl;
      if (url) {
        window.location.href = url;
        return;
      }
      setError(t("page.creditPackages.errorCheckout"));
    } catch (e) {
      setError(t("page.creditPackages.errorCheckout"));
    } finally {
      setSubmitting(false);
    }
  };

  const hasPackages = Array.isArray(packages) && packages.length > 0;

  return (
    <div className="suscriptions-modal-overlay" role="dialog" aria-modal="true">
      <div className="suscriptions-modal-dialog credit-packages-modal">
        <div className="suscriptions-modal-header">
          <div className="suscriptions-modal-title-section">
            <Wallet size={20} aria-hidden="true" stroke="var(--primary)" />
            <h3 className="credit-modal-title">
              {t("page.creditPackages.title")}
            </h3>
          </div>
          <ButtonClose
            onClick={onClose}
            ariaLabel={t("page.creditPackages.cancel")}
            disabled={submitting}
          />
        </div>
        <p className="credit-modal-message">
          {t("page.creditPackages.subtitle")}
        </p>

        {loading ? (
          <p className="credit-packages-loading">
            {t("page.creditPackages.loading")}    
          </p>
        ) : null}

        {!loading && !hasPackages ? (
          <p className="credit-packages-empty">
            {t("page.creditPackages.noPackages")} 
          </p>
        ) : null}

        {!loading && hasPackages ? (
          <div className="credit-packages-form">
            <div className="credit-packages-field">
              <label className="credit-packages-label" htmlFor="credit-package-select">
                {t("page.creditPackages.packageLabel")}  
              </label>
              <Combobox
                id="credit-package-select"
                className="combobox-full"
                value={selectedPackageId}
                onChange={(val) => setSelectedPackageId(String(val))}
                options={packages.map((pkg, index) => {
                  const id = String(pkg.id ?? pkg.credit_package_id ?? index);
                  const name = pkg.name || pkg.display_name || `#${id}`;
                  const credits = pkg.credits ?? pkg.amount_credits;
                  const price = pkg.price ?? pkg.amount_price ?? pkg.unit_amount;
                  const currency = pkg.currency || "USD";
                  const labelParts = [name];
                  if (credits) labelParts.push(`${credits} ${t("page.creditPackages.creditsSuffix")}`);
                  if (price) {
                    const numericPrice = Number(price);
                    const priceText = Number.isFinite(numericPrice)
                      ? numericPrice.toFixed(2)
                      : price;
                    labelParts.push(`${currency} ${priceText}`);
                  }
                  return {
                    value: id,
                    label: labelParts.join(" · "),
                  };
                })}
              />
            </div>

            <div className="credit-packages-field">
              <label className="credit-packages-label" htmlFor="credit-package-quantity">
                {t("page.creditPackages.quantityLabel")} 
              </label>
              <input
                id="credit-package-quantity"
                type="number"
                min={1}
                className="credit-packages-input"
                value={quantity}
                disabled={submitting}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="suscriptions-modal-error">
            <div className="suscriptions-modal-error-title">
              {t("page.creditPackages.errorTitle")} 
            </div>
            <div className="suscriptions-modal-error-message">{error}</div>
          </div>
        ) : null}

        <div className="suscriptions-modal-actions">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={submitting}
            onClick={onClose}
          >
            {t("page.creditPackages.cancel")} 
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={submitting || !hasPackages}
            onClick={handleConfirm}
          >
            {submitting
              ? t("page.creditPackages.loadingCheckout")
              : t("page.creditPackages.confirm")} 
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CreditPackagesModal;
